/**
 * Onboarding Guard - Route protection for dashboard access
 *
 * Checks if user has completed the hard gate (modules 1-3) before
 * allowing access to protected routes. Redirects to /onboarding if not.
 *
 * Flow:
 * 1. Check localStorage for completion status
 * 2. (Future) Verify with backend API
 * 3. If version mismatch, trigger delta flow
 * 4. Allow or redirect accordingly
 */

import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  useOnboardingStore,
  useIsHardGateComplete,
  needsVersionUpdate,
} from '../../stores/onboardingStore';
import { CURRENT_ONBOARDING_VERSION } from '../../lib/onboarding/constants';

interface OnboardingGuardProps {
  /**
   * If true, requires full onboarding completion (all 6 modules).
   * If false (default), only requires hard gate completion (modules 1-3).
   */
  requireFullCompletion?: boolean;
}

export default function OnboardingGuard({
  requireFullCompletion = false,
}: OnboardingGuardProps) {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  const {
    isComplete,
    onboardingVersion,
    role,
  } = useOnboardingStore();

  const isHardGateComplete = useIsHardGateComplete();

  useEffect(() => {
    // Simulate async check (future: API verification)
    const checkOnboardingStatus = async () => {
      // For MVP, we just check localStorage (already done via Zustand)
      // Future: Call /api/me/onboarding-status and reconcile

      // Check for version mismatch
      if (isComplete && needsVersionUpdate(onboardingVersion, CURRENT_ONBOARDING_VERSION)) {
        // User completed an older version - would trigger delta flow
        // For now, we'll redirect to onboarding with a state flag
        console.log('Onboarding version mismatch detected');
      }

      setIsChecking(false);
    };

    checkOnboardingStatus();
  }, [isComplete, onboardingVersion]);

  // Show loading state while checking
  if (isChecking) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--color-bg)' }}
      >
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner />
          <p
            className="text-sm"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Verifying access...
          </p>
        </div>
      </div>
    );
  }

  // Determine if access should be granted
  const hasAccess = requireFullCompletion
    ? isComplete
    : isHardGateComplete;

  // Check for version mismatch requiring delta onboarding
  const needsDeltaOnboarding =
    isComplete && needsVersionUpdate(onboardingVersion, CURRENT_ONBOARDING_VERSION);

  // Redirect to onboarding if not complete or needs delta
  if (!hasAccess || needsDeltaOnboarding) {
    return (
      <Navigate
        to="/onboarding"
        state={{
          from: location,
          isDelta: needsDeltaOnboarding,
          previousVersion: needsDeltaOnboarding ? onboardingVersion : undefined,
        }}
        replace
      />
    );
  }

  // Access granted - render protected content
  return <Outlet />;
}

// ============================================================================
// Loading Spinner Component
// ============================================================================

function LoadingSpinner() {
  return (
    <div className="relative w-10 h-10">
      {/* Outer ring */}
      <div
        className="absolute inset-0 rounded-full border-2 animate-spin"
        style={{
          borderColor: 'var(--color-border)',
          borderTopColor: 'var(--color-accent)',
        }}
      />
      {/* Lambda symbol */}
      <div
        className="absolute inset-0 flex items-center justify-center text-lg font-bold"
        style={{ color: 'var(--color-accent)' }}
      >
        Λ
      </div>
    </div>
  );
}

// ============================================================================
// Export for use in first-visit dashboard tour
// ============================================================================

export { OnboardingGuard };
